import React, { useState, useEffect } from 'react';
import { Loader2, TableProperties } from 'lucide-react';
import { getInfo, createPivotTable } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import MissingChoice from './MissingChoice';

const PivotTableOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  
  const [config, setConfig] = useState({
    index: [],
    columns: [],
    values: '',
    aggfunc: 'sum',
    fill_value: ''
  });

  useEffect(() => {
    loadColumns();
  }, []);

  const loadColumns = async () => {
    try {
      const result = await getInfo();
      setColumns(result.info.column_names || []);
    } catch (error) {
      toast.error(t('form.loadColumnsFailed'));
    } finally {
      setLoading(false);
    }
  };

  // The values dropdown hides columns already taken by rows or columns, but it
  // only hides them: a column picked as the value *first* and taken for rows
  // afterwards stayed selected, invisibly, and the request came back refused
  // with a message naming nothing the user had clicked. Taking a column for
  // rows or columns therefore releases it from values.
  const releaseFromValues = (prev, col) =>
    (prev.values === col ? { values: '' } : {});

  const handleIndexChange = (col) => {
    setConfig(prev => ({
      ...prev,
      ...releaseFromValues(prev, col),
      index: prev.index.includes(col)
        ? prev.index.filter(c => c !== col)
        : [...prev.index, col]
    }));
  };

  const handleColumnsChange = (col) => {
    setConfig(prev => ({
      ...prev,
      ...releaseFromValues(prev, col),
      columns: prev.columns.includes(col)
        ? prev.columns.filter(c => c !== col)
        : [...prev.columns, col]
    }));
  };

  const handleApply = async () => {
    if (config.index.length === 0) {
      toast.error(t('pivot.needIndex'));
      return;
    }
    if (!config.values) {
      toast.error(t('pivot.needValues'));
      return;
    }

    try {
      setApplying(true);
      const result = await createPivotTable({
        index: config.index,
        columns: config.columns.length > 0 ? config.columns : null,
        values: config.values,
        aggfunc: config.aggfunc,
        fill_value: config.fill_value || null
      });
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('pivot.failed'));
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-muted-foreground">
        <TableProperties className="h-5 w-5" />
        <p className="text-sm">Create a cross-tabulation to summarize your data</p>
      </div>

      {/* Row Index */}
      <div>
        <label className="label mb-2 block">{t('pivot.rowIndex')}</label>
        <p className="text-xs text-muted-foreground mb-2">{t('pivot.rowIndexHelp')}</p>
        <div className="flex flex-wrap gap-2 p-3 border border-border rounded-lg bg-muted/30 max-h-32 overflow-y-auto">
          {columns.map(col => (
            <button
              key={col}
              type="button"
              onClick={() => handleIndexChange(col)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                config.index.includes(col)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border hover:border-primary'
              }`}
              data-testid={`pivot-index-${col}`}
            >
              {col}
            </button>
          ))}
        </div>
        {config.index.length > 0 && (
          <p className="text-xs text-primary mt-1">{t('pivot.selected', { columns: config.index.join(', ') })}</p>
        )}
      </div>

      {/* Column Headers */}
      <div>
        <label className="label mb-2 block">{t('pivot.columnHeaders')}</label>
        <p className="text-xs text-muted-foreground mb-2">{t('pivot.columnHeadersHelp')}</p>
        <div className="flex flex-wrap gap-2 p-3 border border-border rounded-lg bg-muted/30 max-h-32 overflow-y-auto">
          {columns.map(col => (
            <button
              key={col}
              type="button"
              onClick={() => handleColumnsChange(col)}
              disabled={config.index.includes(col) || config.values === col}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                config.columns.includes(col)
                  ? 'bg-accent text-accent-foreground'
                  : config.index.includes(col) || config.values === col
                  ? 'opacity-40 cursor-not-allowed bg-muted'
                  : 'bg-card border border-border hover:border-accent'
              }`}
              data-testid={`pivot-col-${col}`}
            >
              {col}
            </button>
          ))}
        </div>
        {config.columns.length > 0 && (
          <p className="text-xs text-accent mt-1">{t('pivot.selected', { columns: config.columns.join(', ') })}</p>
        )}
      </div>

      {/* Values Column */}
      <div>
        <label className="label mb-2 block">{t('pivot.valuesColumn')}</label>
        <p className="text-xs text-muted-foreground mb-2">{t('pivot.valuesHelp')}</p>
        <select
          value={config.values}
          onChange={(e) => setConfig(prev => ({ ...prev, values: e.target.value }))}
          className="select"
          data-testid="pivot-values"
        >
          <option value="">{t('email.selectColumn')}</option>
          {columns
            .filter(col => !config.index.includes(col) && !config.columns.includes(col))
            .map(col => (
              <option key={col} value={col}>{col}</option>
            ))}
        </select>
      </div>

      {/* Aggregation Function */}
      <div>
        <label className="label mb-2 block">{t('pivot.aggFunction')}</label>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {['sum', 'mean', 'count', 'min', 'max', 'first'].map(func => (
            <button
              key={func}
              type="button"
              onClick={() => setConfig(prev => ({ ...prev, aggfunc: func }))}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                config.aggfunc === func
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
              data-testid={`pivot-agg-${func}`}
            >
              {func.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Fill Value */}
      <div>
        <label className="label mb-2 block">{t('pivot.fillEmpty')}</label>
        <input
          type="text"
          value={config.fill_value}
          onChange={(e) => setConfig(prev => ({ ...prev, fill_value: e.target.value }))}
          placeholder={t('pivot.fillPlaceholder')}
          className="input"
          data-testid="pivot-fill-value"
        />
      </div>

      {/* Actions */}
      <MissingChoice reasons={[config.index.length === 0 && t('pivot.needIndex'), !config.values && t('pivot.needValues')]} />

      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <button onClick={onCancel} className="btn-secondary" data-testid="pivot-cancel">
          {t('form.cancel')}
        </button>
        <button
          onClick={handleApply}
          disabled={applying || config.index.length === 0 || !config.values}
          className="btn-primary flex items-center gap-2"
          data-testid="pivot-apply"
        >
          {applying && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('pivot.apply')}
        </button>
      </div>
    </div>
  );
};

export default PivotTableOperation;
